import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Spinner, Alert } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import { useAuth } from '../hooks/useAuth';
import { useRolePermissions } from '../hooks/useRolePermissions';

const Settings: React.FC = () => {
  const { t } = useTranslation('pages', { keyPrefix: 'settings' });
  const { currentNursery, userRolePermissionsLoading } = useAuth();
  const {
    canManageRoles,
    canAccessUserManagement,
    canAssignUsers,
    canAccessNurseryManagement,
    canAccessSettings
  } = useRolePermissions();
  const navigate = useNavigate();

  useEffect(() => {
    if (userRolePermissionsLoading) return;

    if (!canAccessSettings) {
      // No access to settings at all
      return;
    }

    // Determine the first available sub-page
    const nurseryPrefix = currentNursery ? `/nursery/${currentNursery.nursery_id}` : '';

    if (canManageRoles) {
      navigate(`${nurseryPrefix}/settings/roles`, { replace: true });
    } else if (canAccessUserManagement) {
      navigate(`${nurseryPrefix}/settings/users`, { replace: true });
    } else if (canAssignUsers) {
      navigate(`${nurseryPrefix}/settings/assignments`, { replace: true });
    } else if (canAccessNurseryManagement) {
      navigate(`${nurseryPrefix}/settings/nurseries`, { replace: true });
    }
  }, [
    userRolePermissionsLoading,
    canAccessSettings,
    canManageRoles,
    canAccessUserManagement,
    canAssignUsers,
    canAccessNurseryManagement,
    currentNursery,
    navigate
  ]);

  return (
    <Layout title={t('title')}>
      <Container className="py-5">
        <div className="d-flex flex-column align-items-center justify-content-center" role="status" aria-live="polite">
          {userRolePermissionsLoading ? (
            <>
              <Spinner animation="border" variant="primary" />
              <p className="mt-2 text-muted mb-0">{t('loading')}</p>
            </>
          ) : !canAccessSettings ? (
            <Alert variant="danger" role="alert" className="mb-0">
              {t('noAccess')}
            </Alert>
          ) : (
            <>
              <Spinner animation="border" variant="primary" />
              <p className="mt-2 text-muted mb-0">{t('redirecting')}</p>
            </>
          )}
        </div>
      </Container>
    </Layout>
  );
};

export default Settings;
